"use client";

import React, { useEffect, useRef } from 'react';

const features = [
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    zh: '品質保證',
    en: 'Quality Assurance',
    desc: '所有設備均符合國際醫療器材標準，確保品質可靠，安全可信。',
    bg: '#EBF3FA',
    color: '#1E5F8E',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    zh: '專業技術支援',
    en: 'Technical Support',
    desc: '擁有專業技術團隊，提供設備安裝、培訓及維修保養等全面技術支援服務。',
    bg: '#EEF7E6',
    color: '#6AAF3D',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    zh: '客戶為本',
    en: 'Customer-Centric',
    desc: '以客戶需求為核心，提供個性化的產品選配建議及完善的售後服務。',
    bg: '#EBF3FA',
    color: '#2472A4',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    zh: '快速回應',
    en: 'Rapid Response',
    desc: '承諾快速響應客戶需求，確保醫療設備在最短時間內恢復正常運作。',
    bg: '#EEF7E6',
    color: '#6AAF3D',
  },
];

export default function WhyUsSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el, i) => {
              setTimeout(() => el.classList.add('revealed'), i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-white">
      <div className="max-w-8xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16 space-y-4">
          <div className="reveal-on-scroll flex justify-center">
            <span className="section-label">為何選擇我們 · Why Choose Us</span>
          </div>
          <h2 className="reveal-on-scroll reveal-delay-1 font-display font-800 text-foreground" style={{ fontWeight: 800, fontSize: 'clamp(28px, 3.5vw, 44px)' }}>
            專業、可靠、全面的
            <br />
            <span style={{ color: 'var(--color-primary)' }}>醫療器材服務</span>
          </h2>
          <p className="reveal-on-scroll reveal-delay-2 text-muted leading-relaxed" style={{ fontSize: '16px' }}>
            我們以卓越的產品品質和專業的服務水準，成為香港醫療機構信賴的設備合作夥伴。
          </p>
        </div>

        {/* Features — 2×2 grid, not uniform icon-title-desc */}
        <div className="grid md:grid-cols-2 gap-6">
          {features?.map((feat, idx) => (
            <div
              key={feat?.zh}
              className={`reveal-on-scroll reveal-delay-${idx + 1} group flex gap-5 p-7 rounded-2xl border border-border bg-white card-hover`}
            >
              {/* Icon */}
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
                style={{ background: feat?.bg, color: feat?.color }}
              >
                {feat?.icon}
              </div>

              {/* Text */}
              <div className="space-y-2">
                <div className="flex items-baseline gap-2">
                  <h3 className="font-display font-700 text-foreground" style={{ fontWeight: 700, fontSize: '18px' }}>{feat?.zh}</h3>
                  <span className="text-xs text-muted font-500" style={{ fontWeight: 500 }}>{feat?.en}</span>
                </div>
                <p className="text-muted leading-relaxed" style={{ fontSize: '14px' }}>{feat?.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}